<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { AppButton } from '@sunlabde/m2m-library';
import { useBreakpoints } from '@vueuse/core';
import TimelineItem from '../TimelineItem.vue';
import PaymentBox from '@/components/PaymentBox.vue';
import { useMainStore } from '@/store/main';
import { useProductsStore } from '@/store/products';
import { globalBreakpoints } from '@/utilities/Breakpoints';
import type { TimelineItemProps } from '@/models/TimelineItem';
import { EMAIL_REMINDER_BEFORE_END_OF_TRIAL_DURATION } from '@/utilities/Constants.js';
import { useBillwerkStore } from '@/store/billwerk';
import { trackCheckoutChoosePayment } from '@/utilities/tracking';

const emits = defineEmits<{
	(e: 'signupTrackingEvent', name: string): void;
	(e: 'continue'): void;
	(e: 'goBack'): void;
}>();

const isPaymentDataValid = ref(false);
const waitingForPayment = ref(false);

const productsStore = useProductsStore();
const { allowsTrialOrder } = storeToRefs(productsStore);

const mainStore = useMainStore();
const { selectedPlanCategory } = storeToRefs(mainStore);

const { endOfTrialDate } = storeToRefs(useMainStore());
const endOfTrailDateLocaleString = computed(() => endOfTrialDate.value
	.setLocale(locale.value)
	.toLocaleString({ day: '2-digit', month: '2-digit', year: 'numeric' }));

const reminderMailDateLocaleString = computed(() => endOfTrialDate.value
	.minus(EMAIL_REMINDER_BEFORE_END_OF_TRIAL_DURATION)
	.setLocale(locale.value)
	.toLocaleString({ day: '2-digit', month: '2-digit', year: 'numeric' }));

const validateStepSelection = computed(() => isPaymentDataValid.value);

const { locale } = useI18n();

const nextStep = () => {
	emits('continue');
};

const paymentChangeHandler = (isValid: boolean) => {
	isPaymentDataValid.value = isValid;
};

const paymentFormRef = ref<typeof PaymentBox>();

const submitPaymentForm = () => {
	waitingForPayment.value = true;
	paymentFormRef.value?.submit();
};

const billwerkStore = useBillwerkStore();

const onPaymentFormSuccess = () => {
	waitingForPayment.value = false;

	if (billwerkStore.selectedPaymentMethod) {
		trackCheckoutChoosePayment(billwerkStore.selectedPaymentMethod.ID);
	}

	nextStep();
};

const onSelectPaymentMethod = () => {
	emits('signupTrackingEvent', 'selectPaymentMethod');
};

const { t } = useI18n();

const { greaterOrEqual } = useBreakpoints(globalBreakpoints);
const isGreaterOrEqualPhablet = computed(() => greaterOrEqual('phablet').value);

const trialSteps = computed<TimelineItemProps[]>(() => [
	{
		title: t('generic.today'),
		subtitle: t('timeline.fullAccess'),
		color: 'green',
		customClass: 'w-full phablet:w-[11.43%] whitespace-nowrap',
	},
	{
		size: 'sm',
		color: 'green',
		variant: 'solid',
		customClass: 'w-full phablet:w-[11.43%]',
	},
	{
		title: t('subscribe.trial'),
		subtitle: t('timeline.resignAnytime'),
		color: 'green',
		size: 'sm',
		variant: 'solid',
		customClass: 'w-full phablet:w-[11.43%] whitespace-nowrap',
	},
	{
		size: 'sm',
		color: 'green',
		variant: 'solid',
		customClass: 'w-full phablet:w-[11.43%]',
	},
	{
		size: 'sm',
		color: 'green',
		variant: 'solid',
		customClass: 'w-full phablet:w-[11.43%]',
	},
	{
		title: reminderMailDateLocaleString.value,
		subtitle: t('timeline.emailReminder'),
		size: 'sm',
		color: 'green',
		variant: 'solid',
		customClass: 'w-full phablet:w-[11.43%] whitespace-nowrap',
	},
	{
		size: 'sm',
		color: 'green',
		variant: 'solid',
		customClass: 'w-full phablet:w-[11.43%]',
	},
	{
		title: endOfTrailDateLocaleString.value,
		subtitle: t('timeline.firstPayment'),
		color: 'black',
		customClass: 'w-full phablet:w-[20%]',
	},
]);

const trialStepsBasedOnBreakpoints = computed(() => {
	if (isGreaterOrEqualPhablet.value) return trialSteps.value;

	return trialSteps.value.filter((step) => !!step.title);
});
</script>

<template>
	<div>
		<h2 class="text-center text-[24px] mb-4 mx-auto tablet:w-8/12 desktop:w-10/12 widescreen:w-8/12 font-bold font-serif">
			<i18n-t
				v-if="allowsTrialOrder"
				keypath="subscribe.step.plan.title_trial"
				scope="global"
			>
				<template #free>
					<span class="text-secondary-500">
						{{ $t('subscribe.step.plan.title_trial.free') }}
					</span>
				</template>
			</i18n-t>
			<i18n-t
				v-else-if="selectedPlanCategory === 'plus'"
				keypath="subscribe.step.plan.title.plus"
				scope="global"
			>
				<template #name>
					<span class="text-secondary-500">
						{{ $t('planType.plus.name') }}
					</span>
				</template>
			</i18n-t>
			<i18n-t
				v-else
				keypath="subscribe.step.plan.title"
				scope="global"
			>
				<template #voucher_code>
					<span class="text-secondary-500">{{ $t('voucher_code') }}</span>
				</template>
			</i18n-t>
		</h2>
		<p class="text-center mb-8 text-gray-700 leading-normal mx-auto phablet:w-8/12 desktop:w-10/12 widescreen:w-8/12">
			<i18n-t
				v-if="allowsTrialOrder"
				keypath="subscribe.step.plan.subtitle_trial"
				scope="global"
			>
				<template #date>
					<span class="font-bold">
						{{ endOfTrailDateLocaleString }}
					</span>
				</template>
			</i18n-t>
			<i18n-t
				v-else-if="selectedPlanCategory === 'plus'"
				keypath="subscribe.step.plan.subtitle.plus"
				scope="global"
			/>
			<i18n-t
				v-else
				keypath="subscribe.step.plan.subtitle"
				scope="global"
			/>
		</p>
		<div
			v-if="allowsTrialOrder"
			class="flex flex-col mb-8 phablet:flex-row tablet:px-16"
		>
			<TimelineItem
				v-for="trialStep in trialStepsBasedOnBreakpoints"
				:key="trialStep.title"
				:title="trialStep.title"
				:subtitle="trialStep.subtitle"
				:size="trialStep.size"
				:variant="trialStep.variant"
				:direction="isGreaterOrEqualPhablet ? 'horizontal' : 'vertical'"
				:color="trialStep.color"
				:custom-class="trialStep.customClass"
			/>
		</div>
		<div class="block relative">
			<div
				key="paymentSelection"
				class="w-full mb-8"
			>
				<PaymentBox
					ref="paymentFormRef"
					@change="paymentChangeHandler"
					@submit="submitPaymentForm"
					@success="onPaymentFormSuccess"
					@select-payment-method="onSelectPaymentMethod"
				/>
				<div
					key="stepFooter"
					class="mt-8 text-center"
				>
					<AppButton
						:is-disabled="!validateStepSelection"
						:is-loading="waitingForPayment"
						data-cy="button_nextStep"
						class="mx-auto w-full mobile:w-auto"
						@click="submitPaymentForm"
					>
						{{ $t('button.nextStep') }}
					</AppButton>
				</div>
			</div>
		</div>
	</div>
</template>
